import React, {
	useCallback,
} from 'react';
import {
	Card,
	Row,
	Col,
	notification,
} from 'antd';
import {
	useParams,
	useHistory,
} from 'react-router-dom';
import { FixtureCounterpartyTypes } from '@shared/utils/constants';
import type { FixtureCounterpartyProps } from '@api/models/fixture-counterparty';
import type { UpdateBrokerAttributes } from '@api/features/brokers/updateBroker';
import {
	deleteBroker,
	getBrokerDetails,
	updateBroker,
	deleteCounterparty,
	getCounterpartyDetails,
	updateCounterparty,
	deleteSupplier,
	getSupplierDetails,
	updateSupplier,
} from '@client/lib/api';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import showErrorNotification from '@client/utils/showErrorNotification';
import SimpleScreen from '@client/components/screens/SimpleScreen';
import EditableDetails from '@client/components/EditableDetails/EditableDetails';
import getPointOfContactItems from '@client/utils/getPointOfContactItems';
import Button from '@client/components/Button';
import { Links } from '@client/utils/links';
import getItems from './helpers/getItems';

const CounterpartyDetailsScreen = () => {
	const { id, type } = useParams<{ id: string; type: string }>();
	const history = useHistory();

	const counterpartyId = Number(id);

	const [
		counterparties,
		refreshCounterparty,
		error,
		loading,
	] = useFetchedState(() => {
		switch (type) {
			case FixtureCounterpartyTypes.BROKER:
				return getBrokerDetails(counterpartyId);
			case FixtureCounterpartyTypes.SUPPLIER:
				return getSupplierDetails(counterpartyId);
			default:
				return getCounterpartyDetails(counterpartyId);
		}
	},
	[], { showNotification: false });

	const saveDetails = useCallback(async (values: Partial<FixtureCounterpartyProps>) => {
		switch (type) {
			case FixtureCounterpartyTypes.BROKER:
				await updateBroker(counterpartyId, values as UpdateBrokerAttributes);
				break;
			case FixtureCounterpartyTypes.SUPPLIER:
				await updateSupplier(counterpartyId, values);
				break;
			default:
				await updateCounterparty(counterpartyId, values);
		}

		await refreshCounterparty();
	}, [counterpartyId, refreshCounterparty, type]);

	const onDeleteCounterparty = useCallback(async () => {
		try {
			switch (type) {
				case FixtureCounterpartyTypes.BROKER:
					await deleteBroker(counterpartyId);
					break;
				case FixtureCounterpartyTypes.SUPPLIER:
					await deleteSupplier(counterpartyId);
					break;
				default:
					await deleteCounterparty(counterpartyId);
			}

			notification.success({
				message: 'Counterparty was deleted',
				description: `${counterparties?.name} has successfully been deleted.`,
			});

			history.goBack();
		} catch (e) {
			showErrorNotification('Could not delete counterparty', e as Error);
		}
	}, [type, counterparties?.name, history, counterpartyId]);

	return (
		<SimpleScreen
			error={error}
			canGoBack
			breadcrumbs={[['Counterparties', Links.Counterparties.get()]]}
			title={`${counterparties?.name} (${counterparties?.type ?? type})`}
			rootPageTitle="Counterparties"
			loading={loading}
			headerActions={[(
				<Button
					disabled={loading}
					onClick={onDeleteCounterparty}
					confirmTitle={`Are you sure you want to delete ${counterparties?.name}?`}
					key="delete"
					type="primary"
					danger
				>
					Delete counterparty
				</Button>
			)]}
		>
			<Row gutter={16}>
				<Col span={12}>
					<Row gutter={[16, 16]}>
						<Col span={24}>
							<Card bordered>
								<EditableDetails
									items={counterparties != null ? getItems(counterparties) : []}
									onSave={saveDetails}
								/>
							</Card>
						</Col>
						<Col span={24}>
							<Card bordered>
								<EditableDetails
									items={counterparties != null ? getPointOfContactItems(counterparties) : []}
									onSave={saveDetails}
									title="Point of contact"
								/>
							</Card>
						</Col>
					</Row>
				</Col>
			</Row>
		</SimpleScreen>
	);
};

export default CounterpartyDetailsScreen;
